import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Boxes, Clock, ChevronDown, ChevronRight, Edit, Ellipsis, KeyRound, Link2, Lock, LockOpen, MessageCircle, MessageCircleMore, SquareArrowOutUpRight, Star } from "lucide-react";
import { isEmpty } from 'lodash-es';

import { AppStateContext } from '@/state/AppStateContext';
import { addCustomGPTAccess, addCustomGPTUser, getCustomGPTDetails } from "@/api/api";
import { chatModels } from "@/constants/models";
import { CustomGPT } from '@/api/models';
import { categories } from "@/constants/categories";
import { changeCustomGPT, refreshUserCustomGPTs } from "@/utils/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

import sharepointlogo from "@/assets/sharepoint.png";

interface CustomGPTDetailsProps {
  isViewDialogOpen: boolean;
  setIsViewDialogOpen: (isOpen: boolean) => void;
  selectedCustomGPT: CustomGPT | null;
  editable: boolean;
}

const CustomGPTDetails = ({ isViewDialogOpen, setIsViewDialogOpen, selectedCustomGPT, editable }: CustomGPTDetailsProps) => {
  const appStateContext = useContext(AppStateContext);
  const navigate = useNavigate();
  const ADMINS = appStateContext?.state.frontendSettings?.admins || [];
  const handleRefreshUserCustomGPTs = () => refreshUserCustomGPTs(appStateContext);
  const handleCustomGPTChange = (customGPT: CustomGPT) => changeCustomGPT(appStateContext, customGPT);
  const [loading, setLoading] = useState<boolean>(false);
  const [copyLinkText, setCopyLinkText] = useState<string>('Copy Link');
  const [systemPromptExpanded, setSystemPromptExpanded] = useState<boolean>(false);
  const [filesExpanded, setFilesExpanded] = useState<boolean>(false);
  const [filesArr, setFilesArr] = useState<any>([]);
  const [sharepointURLExpanded, setSharepointURLExpanded] = useState<boolean>(false);
  const [sharepointArticlesExpanded, setSharepointArticlesExpanded] = useState<boolean>(false);
  const [accessArr, setAccessArr] = useState<any>([]);
  const [requestExists, setRequestExists] = useState<boolean>(false);
  const [hasAccess, setHasAccess] = useState<boolean>(false);
  const [requestedAccess, setRequestedAccess] = useState<boolean>(false);
  const [startingConversation, setStartingConversation] = useState<boolean>(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const showSecondConvStartersRow = !isEmpty(selectedCustomGPT?.conv_starter1) && !isEmpty(selectedCustomGPT?.conv_starter2) && !isEmpty(selectedCustomGPT?.conv_starter3);

  /** 
   * Handle loading additional Custom GPT details
  **/
  useEffect(() => {
    setFilesArr([]);
    setAccessArr([]);
    setHasAccess(false);
    setRequestExists(false);
    if (selectedCustomGPT) handleGetCustomGPTDetails();
  }, [selectedCustomGPT]);
  
  /** 
   * Handle getting details for the selected Custom GPT
  **/
  const handleGetCustomGPTDetails = () => {
    setLoading(true);
    getCustomGPTDetails(selectedCustomGPT?.id || '').then((res) => {
      // Set files array
      if (res && res.files && res.files.length > 0) setFilesArr(res.files.map((file: any) => file['filename']));
      else setFilesArr([]);

      // Set access array
      if (res && res.access && res.access.length > 0) {
        setAccessArr(res.access.map((access: any) => {
          // Check if current user has access
          if (access['userId'] == appStateContext?.state.currentUser?.id && access['granted'] == 1) setHasAccess(true);
          // Check if current user has requested access
          else if (access['userId'] == appStateContext?.state.currentUser?.id && access['granted'] == 0) setRequestExists(true);
          // Check if user is an admin
          else if (ADMINS.includes(appStateContext?.state.currentUser?.id || '')) setHasAccess(true);
          
          return access;
        }));
      }
      else setAccessArr([]);

      // If current user created Custom GPT, they have access
      if (selectedCustomGPT?.created_by == appStateContext?.state.currentUser?.id) setHasAccess(true);

      setLoading(false);
    }).catch((err) => {
      console.error('Error fetching details:', err);
      setFilesArr([]);
      setAccessArr([]);
      setHasAccess(false);
      setRequestExists(false);
      setLoading(false);
    });
  }
  
  /** 
   * Handle formatting number into shorter string
  **/
  const formatNumber = (num: number) => {
    if (num >= 1000000) return Math.floor(num / 1000000) + 'M+';
    else if (num >= 1000) return Math.floor(num / 1000) + 'K+';
    else return num;
  }

  /** 
   * Handle starting a conversation with a Custom GPT with the selected conversation starter
  **/
  const useConvStarter = async (starter: string) => {
    if (!startingConversation && selectedCustomGPT && (selectedCustomGPT?.visibility != 1 || hasAccess)) {
      setStartingConversation(true);
      
      // Add selected Custom GPT to current user
      await addCustomGPTUser(selectedCustomGPT);

      // Refresh the user's Custom GPT list in sidebar
      handleRefreshUserCustomGPTs();

      // Set the selected conversation starter
      appStateContext?.dispatch({ type: 'SET_SELECTED_CONVERSATION_STARTER', payload: starter });

      // Navigate back to chat and set selected Custom GPT
      setStartingConversation(false);
      navigate('/');
      handleCustomGPTChange(selectedCustomGPT);
      setIsViewDialogOpen(false);
    }
  }

  /** 
   * Handle starting a conversation with a Custom GPT
   * Adding the Custom GPT to the user's list and navigating to chat
  **/
  const handleStartConversation = async () => {
    if (selectedCustomGPT) {
      setStartingConversation(true);
      
      // Add selected Custom GPT to current user
      await addCustomGPTUser(selectedCustomGPT);

      // Refresh the user's Custom GPT list in sidebar
      handleRefreshUserCustomGPTs();

      // Navigate back to chat and set selected Custom GPT
      setStartingConversation(false);
      navigate('/');
      handleCustomGPTChange(selectedCustomGPT);
      setIsViewDialogOpen(false);
    }
  }

  /** 
   * Handle adding access request for Custom GPT
  **/
  const handleAddAccess = async () => {
    if (selectedCustomGPT) {
      setRequestedAccess(true);

      // Add access request for selected Custom GPT for current user
      addCustomGPTAccess(selectedCustomGPT.id, appStateContext?.state.currentUser?.id || '', appStateContext?.state.currentUser?.name || '', 0, 1);

      // Wait 2 seconds before closing the dialog
      setTimeout(() => {
        // Close the dialog
        setIsViewDialogOpen(false);
        setRequestedAccess(false);
      }, 2000);
    }
  }

  /** 
   * Handle opening the edit dialog for a Custom GPT
  **/
  const onEdit = () => {
    navigate('/spaces/e/' + selectedCustomGPT?.id);
  }

  /** 
   * Handle copying the link to the Custom GPT
  **/
  const onCopyLink = () => {
    setCopyLinkText('Copied Link');
    navigator.clipboard.writeText(window.location.origin + "/spaces/v/" + selectedCustomGPT?.id);
    setTimeout(() => {
      setCopyLinkText('Copy Link');
      setIsPopoverOpen(false);
    }, 3000);
  }

  /** 
   * Helper function to capitalize the first letter of first two words of Custom GPT name, if it does not have an icon image
   * 
   * @param {string} inputString - The Custom GPT's name
  **/
  const lettersAsIcon = (inputString: string) => {
    const words = inputString.split(' ');
    const initials = [];

    if (words.length >= 1) initials.push(words[0].charAt(0).toUpperCase());
    if (words.length >= 2) initials.push(words[1].charAt(0).toUpperCase());

    return initials.join('');
  }

  return (
    <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
      <DialogContent className="flex flex-col max-h-[calc(100vh-4rem)] max-w-full w-full lg:w-1/2 bg-secondary border-transparent rounded-3xl shadow-short p-4" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle />
        </DialogHeader>

        <div className="absolute top-4 right-12">
          <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
            <PopoverTrigger>
              <Ellipsis size={18} />
            </PopoverTrigger>
            <PopoverContent className="bg-secondary border-transparent rounded-3xl shadow-short w-[175px] p-3 me-12">
              <div className="flex items-center gap-3 hover:bg-neutral rounded-lg cursor-pointer py-2 px-3" onClick={onCopyLink}>
                <Link2 className="" size={16} />
                <div className="select-none text-sm">{copyLinkText}</div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex flex-col max-h-[90vh] overflow-x-hidden scroll-on-hover scrollbar scrollbar-gutter-stable scrollbar-thumb-accent scrollbar-track-transparent scrollbar-w-8 px-4 mb-2">
          <div className="flex-1">
            <div className="flex flex-col items-center">
              {!isEmpty(selectedCustomGPT?.image_path) ?
                <img src={`https://asappsmsstore.blob.core.windows.net/customgpticons/${selectedCustomGPT?.image_path}`} className="w-24 h-24 rounded-full object-cover" alt={selectedCustomGPT?.name} />
              :
                <div className="avatar static placeholder">
                  <div className="bg-secondary w-24 rounded-full">
                    <span className="text-3xl">{lettersAsIcon(selectedCustomGPT?.name || '')}</span>
                  </div>
                </div>
              }

              <h1 className="text-3xl dark:text-white font-bold my-4">{selectedCustomGPT?.name || ''}</h1>
              {!selectedCustomGPT?.pinned && <h6 className="text-sm mb-3">By: <strong>{selectedCustomGPT?.created_by_name || ''}</strong> {!isEmpty(selectedCustomGPT?.co_creator_name) ? <> & <strong>{selectedCustomGPT?.co_creator_name}</strong></> : ``}</h6>}
              {categories.findIndex(category => category.id == selectedCustomGPT?.category) > -1 &&
                <div className="badge badge-outline border-primary text-dark dark:text-white pt-3 pb-3.5 px-4 mb-2">{categories[categories.findIndex(category => category.id == selectedCustomGPT?.category)].name || ''}</div>
              }
              <h6 className="text-center text-sm">{selectedCustomGPT?.description || ''}</h6>

              <div className="flex w-full mt-4 mb-2">
                {(selectedCustomGPT?.rating_count || 0) > 0 ? 
                  <>
                    <div className="flex flex-grow items-center justify-center w-1/4">
                      <div className="flex flex-col items-center gap-1">
                        <div className="flex items-center gap-1">
                          <Star size={20} className="text-accent" fill="currentColor" />
                          <span className="font-bold">{selectedCustomGPT?.rating || '0'} <span className="text-xs text-accent"> / 5</span></span>
                        </div>
                        <div className="text-sm">Ratings ({formatNumber(selectedCustomGPT?.rating_count || 0)})</div>
                      </div>
                    </div>
                    <div className="divider divider-horizontal"></div>
                  </>
                : null}
                <div className="flex flex-grow items-center justify-center w-1/4">
                  {selectedCustomGPT?.visibility == 2 ? 
                    <div className="flex flex-col items-center gap-1">
                      <Lock size={20} className="text-accent" />
                      <div className="font-bold">Private</div>
                    </div>
                  : (selectedCustomGPT?.visibility == 1 ? 
                    <div className="flex flex-col items-center gap-1">
                      <KeyRound size={20} className="text-accent" />
                      <div className="font-bold">Restricted</div>
                    </div>
                  : 
                    <div className="flex flex-col items-center gap-1">
                      <LockOpen size={20} className="text-accent" />
                      <div className="font-bold">Public</div>
                    </div>
                  )}
                </div>
                <div className="divider divider-horizontal"></div>
                <div className="flex flex-grow items-center justify-center w-1/4">
                  <div className="flex flex-col items-center gap-1">
                    <Boxes size={20} className="text-accent" />
                    <div className="font-bold">{chatModels.find(model => model.id === selectedCustomGPT?.default_model)?.name || chatModels.find(model => model.default === true)?.name || ''}</div>
                  </div>
                </div>
                {(selectedCustomGPT?.conversation_count || 0) > 0 ? 
                  <>
                    <div className="divider divider-horizontal"></div>
                    <div className="flex flex-grow items-center justify-center w-1/4">
                      <div className="flex flex-col items-center gap-1">
                        <div className="flex items-center gap-1">
                        <MessageCircleMore size={20} className="text-accent" />
                          <span className="font-bold">{formatNumber(selectedCustomGPT?.conversation_count || 0)}</span>
                        </div>
                        <div className="text-sm">Conversations</div>
                      </div>
                    </div>
                  </>
                : null}
              </div>

              {!isEmpty(selectedCustomGPT?.conv_starter1) || !isEmpty(selectedCustomGPT?.conv_starter2) || !isEmpty(selectedCustomGPT?.conv_starter3) || !isEmpty(selectedCustomGPT?.conv_starter4) ?
                <>
                  <div className="dark:text-white font-bold w-full px-1 mt-4 mb-2">Conversation Starters</div>
                  <div className={`grid grid-cols-2 items-stretch ${showSecondConvStartersRow ? 'grid-rows-2' : 'grid-rows-1'} w-full gap-4`}>
                    {selectedCustomGPT?.conv_starter1 ?
                      <div className={`border border-accent bg-neutral rounded-lg ${((selectedCustomGPT?.visibility != 1 || hasAccess) ? `cursor-pointer` : ``)} text-sm px-4 py-2`} onClick={() => useConvStarter(selectedCustomGPT?.conv_starter1 || '')}>
                        <pre className="whitespace-pre-wrap break-keep">{selectedCustomGPT?.conv_starter1}</pre>
                      </div>
                    : null}
                    {selectedCustomGPT?.conv_starter2 ?
                      <div className={`border border-accent bg-neutral rounded-lg ${((selectedCustomGPT?.visibility != 1 || hasAccess) ? `cursor-pointer` : ``)} text-sm px-4 py-2`} onClick={() => useConvStarter(selectedCustomGPT?.conv_starter2 || '')}>
                        <pre className="whitespace-pre-wrap break-keep">{selectedCustomGPT?.conv_starter2}</pre>
                      </div>
                    : null}
                    {selectedCustomGPT?.conv_starter3 ?
                      <div className={`border border-accent bg-neutral rounded-lg ${((selectedCustomGPT?.visibility != 1 || hasAccess) ? `cursor-pointer` : ``)} text-sm px-4 py-2`} onClick={() => useConvStarter(selectedCustomGPT?.conv_starter3 || '')}>
                        <pre className="whitespace-pre-wrap break-keep">{selectedCustomGPT?.conv_starter3}</pre>
                      </div>
                    : null}
                    {selectedCustomGPT?.conv_starter4 ?
                      <div className={`border border-accent bg-neutral rounded-lg ${((selectedCustomGPT?.visibility != 1 || hasAccess) ? `cursor-pointer` : ``)} text-sm px-4 py-2`} onClick={() => useConvStarter(selectedCustomGPT?.conv_starter4 || '')}>
                        <pre className="whitespace-pre-wrap break-keep">{selectedCustomGPT?.conv_starter4}</pre>
                      </div>
                    : null}
                  </div>
                </>
              : null}

              <div className="w-full mt-4">
                <button type="button" className="btn btn-sm btn-ghost justify-start dark:text-white px-0" onClick={() => setSystemPromptExpanded(!systemPromptExpanded)}>{systemPromptExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />} System Prompt</button>
                <div className={`transition-all duration-300 overflow-hidden ${systemPromptExpanded ? 'opacity-100 mt-2' : 'max-h-0 opacity-0'}`}>
                  <div className="border border-accent bg-neutral rounded-xl text-sm px-3 py-2 m-1"><pre className="whitespace-pre-wrap break-keep">{selectedCustomGPT?.system_prompt}</pre></div>
                </div>
              </div>

              {filesArr.length > 0 ?
                <div className="w-full mt-4">
                  <button type="button" className="btn btn-sm btn-ghost justify-start dark:text-white px-0" onClick={() => setFilesExpanded(!filesExpanded)}>{filesExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />} Files</button>
                  <div className={`transition-all duration-300 overflow-hidden ${filesExpanded ? 'opacity-100 mt-2' : 'max-h-0 opacity-0'}`}>
                    <div className="flex flex-grow flex-wrap gap-2">
                      {filesArr.map((file: any, idx: number) => 
                        <div key={idx} className="border border-accent bg-neutral rounded-lg text-xs px-3 py-1">{file}</div>
                      )}
                   </div>
                  </div>
                </div>
              : null}
              
              {(() => {
                const spUrls = selectedCustomGPT?.sharepointURLs?.length
                  ? selectedCustomGPT.sharepointURLs
                  : selectedCustomGPT?.sharepointURL
                  ? [selectedCustomGPT.sharepointURL]
                  : [];
                return spUrls.length > 0 ? (
                  <div className="w-full mt-4">
                    <button type="button" className="btn btn-sm btn-ghost justify-start dark:text-white px-0" onClick={() => setSharepointURLExpanded(!sharepointURLExpanded)}>{sharepointURLExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />} SharePoint Document Library</button>
                    <div className={`transition-all duration-300 overflow-hidden ${sharepointURLExpanded ? 'opacity-100 mt-2' : 'max-h-0 opacity-0'}`}>
                      {spUrls.map((url: string, idx: number) => (
                        <div key={idx} className="flex items-center justify-between border border-accent bg-neutral rounded-lg cursor-pointer text-sm text-center break-all gap-2 p-1 mx-1 mt-1" onClick={() => window.open(url, '_blank')}>
                          <img src={sharepointlogo} alt="SharePoint" className="w-16 shrink-0" />
                          <div>{url}</div>
                          <SquareArrowOutUpRight size={20} className="me-2 shrink-0" />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null;
              })()}
              
              {!isEmpty(selectedCustomGPT?.sharepointArticles) ?
                <div className="w-full mt-4">
                  <button type="button" className="btn btn-sm btn-ghost justify-start dark:text-white px-0" onClick={() => setSharepointArticlesExpanded(!sharepointArticlesExpanded)}>{sharepointArticlesExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />} SharePoint Intranet Articles</button>
                  <div className={`transition-all duration-300 overflow-hidden ${sharepointArticlesExpanded ? 'opacity-100 mt-2' : 'max-h-0 opacity-0'}`}>
                    {selectedCustomGPT?.sharepointArticles?.split('|').map((article: string, idx: number) =>
                      <div key={idx} className="flex items-center justify-between border border-accent bg-neutral rounded-lg cursor-pointer text-sm text-center break-all gap-2 p-1 mx-1 mt-1" onClick={() => window.open(article, '_blank')}>
                        <img src={sharepointlogo} alt="SharePoint" className="w-16 shrink-0" />
                        <div>{article}</div>
                        <SquareArrowOutUpRight size={20} className="me-2 shrink-0" />
                      </div>
                    )}
                  </div>
                </div>
              : null}
            </div>
          </div>
          
          <div className="flex flex-grow items-center justify-center w-full gap-4 mt-6 mb-2">
            {loading ?
              <span className="loading loading-ring text-primary"></span>
            :
              <>
                {(selectedCustomGPT?.visibility == 1 && !hasAccess && !requestExists) ?
                  <button type="button" className="btn btn-outline btn-primary flex-grow" onClick={handleAddAccess} disabled={requestedAccess}>{requestedAccess ? `Access Requested` : `Request Access`}</button>
                : 
                  ((selectedCustomGPT?.visibility == 1 && requestExists) ?
                    <div className="flex items-center gap-3 bg-accent rounded py-2 px-4"><Clock size={20} /> Your access request is awaiting approval</div>
                  :
                    <button type="button" className="btn btn-primary flex-grow" onClick={handleStartConversation} disabled={startingConversation}>{startingConversation ? <span className="loading loading-dots loading-lg text-primary"></span> : <><MessageCircle size={20} /> Start Conversation</>}</button>
                )}
                {(editable && ((!selectedCustomGPT?.pinned && (selectedCustomGPT?.created_by === appStateContext?.state.currentUser?.id || selectedCustomGPT?.co_creator === appStateContext?.state.currentUser?.id)) || ADMINS.includes(appStateContext?.state.currentUser?.id || ''))) ?
                  <button type="button" className="btn btn-success" onClick={onEdit}><Edit size={20} /> Edit</button>
                : null}
              </>
            }
          </div>
        </div>

      </DialogContent>
    </Dialog>
  );
}

export default CustomGPTDetails;
